// ----------------------------------------------------------------------
// Shared localStorage key helpers — used by useSubscription, PlanSelectionModal, and callback pages
// ----------------------------------------------------------------------

export const optimisticCancelKey = (id: string) => `__optimistic_cancel_${id}`;

export const pendingPlanKey = (id: string) => `__pending_billing_plan_${id}`;

export const CANCEL_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

export const PENDING_PLAN_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

export function readPendingPlan(orgId: string): string | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(pendingPlanKey(orgId));
  if (!raw) return null;
  try {
    const { plan, ts } = JSON.parse(raw) as { plan: string; ts: number };
    if (Date.now() - ts < PENDING_PLAN_EXPIRY_MS) return plan;
    localStorage.removeItem(pendingPlanKey(orgId));
  } catch {
    localStorage.removeItem(pendingPlanKey(orgId));
  }
  return null;
}

export const pendingCouponKey = (id: string) => `__pending_billing_coupon_${id}`;

/**
 * The coupon a user carried into Stripe Checkout, remembered across the redirect so the success
 * page can name it before the webhook has landed. Display only — org-status stays the source of
 * truth, and the same 5-minute expiry as the pending plan keeps an abandoned checkout from
 * leaving stale copy behind.
 */
export type PendingCoupon = {
  code: string;
  freeMonths: number;
  requireCard: boolean;
};

export function readPendingCoupon(orgId: string): PendingCoupon | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(pendingCouponKey(orgId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingCoupon & { ts: number };
    if (Date.now() - parsed.ts < PENDING_PLAN_EXPIRY_MS) {
      return { code: parsed.code, freeMonths: parsed.freeMonths, requireCard: parsed.requireCard };
    }
    localStorage.removeItem(pendingCouponKey(orgId));
  } catch {
    localStorage.removeItem(pendingCouponKey(orgId));
  }
  return null;
}

export function writePendingCoupon(orgId: string, coupon: PendingCoupon): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(pendingCouponKey(orgId), JSON.stringify({ ...coupon, ts: Date.now() }));
}

export function clearPendingCoupon(orgId: string): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(pendingCouponKey(orgId));
}

// ----------------------------------------------------------------------

export type BillingState = 'free' | 'active' | 'cancelled' | 'expired';

export type SubscriptionWarning =
  | 'past_due'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused'
  | 'trialing'
  | 'card_expiring'
  | null;

/**
 * Whether a warning is something the customer must fix before a billing action will work.
 *
 * `SubscriptionWarning` doubles as a "notable status" flag, so it also carries `trialing` and
 * `card_expiring` — neither of which stops a code from being applied. Gating on the whole union
 * disabled the redeem card for every organization on free months (a coupon trial is `trialing`
 * by definition) and told them to fix a payment method that was never the problem.
 */
export function isBlockingSubscriptionWarning(warning: SubscriptionWarning): boolean {
  return (
    warning === 'past_due' ||
    warning === 'unpaid' ||
    warning === 'incomplete' ||
    warning === 'incomplete_expired' ||
    warning === 'paused'
  );
}

export type BillingBannerVariant = 'upgrade' | 'reactivate';

export const BILLING_INTERVALS = ['month', 'year'] as const;
export type BillingInterval = typeof BILLING_INTERVALS[number];

// ----------------------------------------------------------------------
// Plan access / feature gating types
// ----------------------------------------------------------------------

export type FeatureKey =
  | 'candidates'
  | 'screenings'
  | 'interviews'
  | 'coding_rounds'
  | 'job_posts'
  | 'ai_interviews'
  | 'custom_branding'
  | 'remove_watermark'
  | 'automations'
  | 'reports';

export type PlanLimitError = {
  code: 'PLAN_LIMIT_EXCEEDED' | 'FEATURE_NOT_AVAILABLE';
  feature: string;
  featureLabel: string;
  limit?: number;
  used?: number;
  remaining?: number;
  currentPlan: string;
  upgradeTo?: string;
  requiredPlan?: string;
  message: string;
  canBuyPack: boolean;
};

export const isPlanLimitError = (
  err: unknown
): err is { response: { data: PlanLimitError } } => {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return d.code === 'PLAN_LIMIT_EXCEEDED' || d.code === 'FEATURE_NOT_AVAILABLE';
};

export const extractPlanLimitError = (err: unknown): PlanLimitError | null => {
  if (!isPlanLimitError(err)) return null;
  const raw = err.response.data as Record<string, unknown>;
  return {
    code: raw.code as PlanLimitError['code'],
    feature: String(raw.feature ?? ''),
    featureLabel: String(raw.featureLabel ?? raw.FeatureLabel ?? ''),
    limit: raw.limit != null ? Number(raw.limit) : undefined,
    used: raw.used != null ? Number(raw.used) : undefined,
    remaining: raw.remaining != null ? Number(raw.remaining) : undefined,
    currentPlan: String(raw.currentPlan ?? raw.CurrentPlan ?? ''),
    upgradeTo: raw.upgradeTo != null ? String(raw.upgradeTo) : undefined,
    requiredPlan: raw.requiredPlan != null ? String(raw.requiredPlan) : undefined,
    message: String(raw.message ?? raw.Message ?? ''),
    canBuyPack: Boolean(raw.canBuyPack ?? raw.CanBuyPack ?? false),
  };
};

export type AccessCheckResult = {
  allowed: boolean;
  limit: number;
  used: number;
  remaining: number;
  percentage: number;
  isApproachingLimit: boolean;
  warningMessage?: string;
};

// Single source of truth for credit-backed features — mirrors backend
// PackEnabledFeatures. The slug `interviews` is internal; the user-facing
// label is "Core Interviews" (see FEATURE_LABELS in usePlanAccess), and it covers every
// interview format including the technical ones, which is how billing sells it.
export const PACK_ENABLED_FEATURES = ['interviews', 'screenings'] as const;
export type PackEnabledFeature = typeof PACK_ENABLED_FEATURES[number];

export const isPackEnabledFeature = (f: string): f is PackEnabledFeature =>
  (PACK_ENABLED_FEATURES as readonly string[]).includes(f);

// ── Pack-purchase error narrowing ──────────────────────────────────────────
// Mirrors the backend's pack-purchase error codes so the modal's switch-on-code
// is compile-time exhaustive instead of relying on hand-typed string literals.

export const PACK_PURCHASE_ERROR_CODES = [
  'PACK_PURCHASE_REQUIRES_PAID_PLAN',
  'PACK_PURCHASE_BLOCKED_DURING_TRIAL',
  'PACK_PURCHASE_IN_PROGRESS',
  'INVALID_CREDIT_QUANTITY',
] as const;

export type PackPurchaseErrorCode = typeof PACK_PURCHASE_ERROR_CODES[number];

const isPackPurchaseErrorCode = (v: unknown): v is PackPurchaseErrorCode =>
  typeof v === 'string' && (PACK_PURCHASE_ERROR_CODES as readonly string[]).includes(v);

export type PackPurchaseErrorInfo = {
  /** Recognized backend code, or null when the error doesn't match the union. */
  code: PackPurchaseErrorCode | null;
  message: string;
};

/**
 * Narrows an arbitrary thrown value into `{ code, message }` for the
 * CreditPurchaseModal switch table. Code is `null` for network / 5xx /
 * unknown payload shapes; the modal falls through to its default branch.
 */
export const extractPackPurchaseError = (err: unknown): PackPurchaseErrorInfo => {
  const data = (err as { response?: { data?: unknown } } | undefined)?.response?.data as
    | Record<string, unknown>
    | undefined;
  const rawCode = data?.code ?? data?.Code;
  const code = isPackPurchaseErrorCode(rawCode) ? rawCode : null;
  const message =
    (data?.message as string | undefined) ||
    (data?.Message as string | undefined) ||
    (err as { message?: string } | undefined)?.message ||
    'Something went wrong. Please try again.';
  return { code, message };
};

// ── Coupons ────────────────────────────────────────────────────────────────

/** One per-feature credit grant on a coupon. */
export type CouponCredit = {
  feature: PackEnabledFeature;
  quantity: number;
  /** How long the credits last once granted. Null = they never expire. */
  expiryMonths: number | null;
  /**
   * False when the current plan cannot spend these credits at all — AI interview endpoints are
   * gated on the plan before any credit is consulted, so a free or starter org would be holding
   * something unusable.
   */
  usableOnCurrentPlan: boolean;
  /** Cheapest plan that can spend them. Null when the current plan already can. */
  requiresPlan: string | null;
};

/** What a code would do, from POST /api/billing/coupons/preview. */
export type CouponPreview = {
  valid: boolean;
  reason: CouponErrorCode | null;
  message: string | null;
  code: string | null;
  description: string | null;
  freeMonths: number;
  requireCard: boolean;
  /** Plans the free months apply to. Empty = any paid plan. */
  appliesToPlans: string[];
  credits: CouponCredit[];
  /** 'checkout' = pick a plan first; 'redeem' = applies to the live subscription. */
  applyVia: 'checkout' | 'redeem';
};

/** Result of POST /api/billing/coupons/redeem. */
export type CouponRedeemResult = {
  applied: boolean;
  freeMonthsEnd: string | null;
  freeMonthsApplied: number;
  creditsGranted: CouponCredit[];
  message: string | null;
};

/** How the current trial came about — decides banner wording and whether top-ups are allowed. */
export const TRIAL_KINDS = ['events', 'coupon', 'staff'] as const;
export type TrialKind = typeof TRIAL_KINDS[number];

export const COUPON_ERROR_CODES = [
  'NOT_FOUND',
  'INACTIVE',
  'NOT_STARTED',
  'EXPIRED',
  'EXHAUSTED',
  'ORG_LIMIT_REACHED',
  'PLAN_NOT_ELIGIBLE',
  'REQUIRES_CHECKOUT',
  'SUBSCRIPTION_CANCELING',
  'PAYMENT_PAST_DUE',
  'NOTHING_TO_GRANT',
  'NOT_USABLE_ON_CURRENT_PLAN',
  'FREE_MONTHS_ACTIVE',
  'REDEEM_IN_PROGRESS',
] as const;

export type CouponErrorCode = typeof COUPON_ERROR_CODES[number];

export const isCouponErrorCode = (v: unknown): v is CouponErrorCode =>
  typeof v === 'string' && (COUPON_ERROR_CODES as readonly string[]).includes(v);

export type CouponErrorInfo = {
  /** Recognized backend reason, or null for network / unknown payloads. */
  code: CouponErrorCode | null;
  httpStatus: number | null;
  message: string;
};

/**
 * Narrows a thrown coupon error into `{ code, httpStatus, message }`.
 * The reason lives under `reason` on the coupon endpoints; `code`/`error` are also read because
 * /subscribe reports its failures under a different key.
 */
export const extractCouponError = (err: unknown): CouponErrorInfo => {
  const response = (err as { response?: { data?: unknown; status?: number } } | undefined)?.response;
  const data = response?.data as Record<string, unknown> | undefined;

  const rawReason = data?.reason ?? data?.Reason ?? data?.code ?? data?.Code ?? data?.error ?? data?.Error;
  const code = isCouponErrorCode(rawReason) ? rawReason : null;

  // 429 comes from the coupon rate limits (preview and redeem here; subscribe's own errors go
  // through extractSubscribeError) and carries no message of ours — in Development no body at
  // all, elsewhere the generic error page — so axios's own text would show.
  const message =
    (response?.status === 429 ? 'Too many attempts. Wait a minute and try again.' : null) ||
    (data?.message as string | undefined) ||
    (data?.Message as string | undefined) ||
    (err as { message?: string } | undefined)?.message ||
    'Something went wrong. Please try again.';

  return { code, httpStatus: response?.status ?? null, message };
};

// ── Subscribe error narrowing ──────────────────────────────────────────────
// /subscribe returns 403/400 with `{ error, message }`. Without this the UI showed the raw code
// string as the toast body.

export const SUBSCRIBE_ERROR_CODES = [
  'PLAN_CHANGE_BLOCKED_DURING_TRIAL',
  'COUPON_REQUIRES_REDEEM',
  'COUPON_INVALID',
] as const;

export type SubscribeErrorCode = typeof SUBSCRIBE_ERROR_CODES[number];

export type SubscribeErrorInfo = {
  code: SubscribeErrorCode | null;
  /** Coupon reason, when the failure was a coupon one. */
  couponReason: CouponErrorCode | null;
  message: string;
  trialEndsAt: string | null;
};

export const extractSubscribeError = (err: unknown): SubscribeErrorInfo => {
  const data = (err as { response?: { data?: unknown } } | undefined)?.response?.data as
    | Record<string, unknown>
    | undefined;

  const rawCode = data?.error ?? data?.Error ?? data?.code ?? data?.Code;
  const code = (SUBSCRIBE_ERROR_CODES as readonly string[]).includes(rawCode as string)
    ? (rawCode as SubscribeErrorCode)
    : null;

  const rawReason = data?.reason ?? data?.Reason;

  const status = (err as { response?: { status?: number } } | undefined)?.response?.status;

  return {
    code,
    couponReason: isCouponErrorCode(rawReason) ? rawReason : null,
    message:
      // Subscribe is rate limited with the coupon endpoints; a 429 carries no message of ours.
      (status === 429 ? 'Too many attempts. Wait a minute and try again.' : null) ||
      (data?.message as string | undefined) ||
      (data?.Message as string | undefined) ||
      (err as { message?: string } | undefined)?.message ||
      'Something went wrong. Please try again.',
    trialEndsAt: (data?.trialEndsAt ?? data?.TrialEndsAt ?? null) as string | null,
  };
};
